/**
 * @file Recherche les clients connus qui ressemblent au brouillon d'identité en cours de saisie.
 * @module packages.common.components.customer.hooks.useCustomerMatches
 *
 * Deux niveaux de recherche :
 *   - principale : tous les champs saisis doivent correspondre à la même fiche ;
 *   - de secours, seulement si la principale ne trouve rien : l'email ou le
 *     téléphone complet, seul. Elle révèle une fiche qui utilise déjà ce contact
 *     sous un autre nom (faute de frappe, ou contact partagé dans un couple),
 *     sans jamais élargir la recherche sur le nom ou le prénom, trop peu
 *     discriminants.
 */

import {useEffect, useMemo} from 'react';

import {useSearchCustomers} from 'packages/common/hooks/api/app/customers/Customer';
import {useDebounce} from 'packages/common/hooks/useDebounce';

import {buildCustomerSearchTerm, buildCustomerContactTerms, sharesDraftEmail, sharesDraftPhone} from '../utils/customerIdentity';

/** Délai avant d'interroger le serveur, pour ne pas partir à chaque frappe. */
const SEARCH_DEBOUNCE_MS = 250;

/**
 * Moyen de contact par lequel la recherche de secours a trouvé des fiches.
 * @enum {string}
 */
export const SHARED_CONTACT = Object.freeze({
  EMAIL: 'email',
  PHONE: 'phone',
  BOTH: 'both',
});

/**
 * Recherche serveur d'un terme, différée pendant la frappe.
 *
 * @param {string} companyId - Identifiant de l'établissement
 * @param {string} term - Terme à rechercher, vide pour suspendre
 * @return {{results: Array<Object>, isLoading: boolean}}
 */
function useDebouncedCustomerSearch(companyId, term) {
  const {searchResults, isLoading, quickSearch, clearResults} = useSearchCustomers(companyId);
  const debouncedTerm = useDebounce(term, SEARCH_DEBOUNCE_MS);

  useEffect(() => {
    if (debouncedTerm) {
      quickSearch(debouncedTerm);
    } else {
      clearResults();
    }
  }, [debouncedTerm, quickSearch, clearResults]);

  const isActive = Boolean(term);
  return {
    results: isActive ? searchResults : [],
    // Entre la frappe et le retour serveur, la liste précédente est périmée :
    // on l'annonce comme chargement pour ne pas afficher « aucun client » à tort.
    isLoading: isActive && (isLoading || term !== debouncedTerm),
  };
}

/**
 * Hook de correspondances client pilotées par les champs du brouillon.
 *
 * @param {string} companyId - Identifiant de l'établissement
 * @param {Object|null} draft - Brouillon d'identité, null pour suspendre la recherche
 * @return {Object} `matches` (fiches proposées), `sharedContact` (valeur de
 *   SHARED_CONTACT quand `matches` vient de la recherche de secours, null sinon),
 *   `isLoading`, et `isActive` (une saisie suffisante est en cours de recherche)
 */
export function useCustomerMatches(companyId, draft) {
  const term = useMemo(() => (draft ? buildCustomerSearchTerm(draft) : ''), [draft]);
  const contactTerms = useMemo(() => buildCustomerContactTerms(draft), [draft]);

  const main = useDebouncedCustomerSearch(companyId, term);
  // Le terme d'un contact ne change pas pendant qu'on tape le nom : ces deux
  // recherches ne repartent qu'à la modification de l'email ou du téléphone.
  const byEmail = useDebouncedCustomerSearch(companyId, contactTerms.email);
  const byPhone = useDebouncedCustomerSearch(companyId, contactTerms.phone);

  const isActive = Boolean(term);
  const hasMainMatches = main.results.length > 0;

  const fallback = useMemo(() => {
    if (!isActive || hasMainMatches) return {owners: [], sharedContact: null};
    // Le serveur cherche par préfixes de mots : seules les fiches dont le
    // contact coïncide exactement avec la saisie sont retenues.
    const emailOwners = byEmail.results.filter((customer) => sharesDraftEmail(customer, draft));
    const phoneOwners = byPhone.results.filter((customer) => sharesDraftPhone(customer, draft));
    const owners = [...new Map([...emailOwners, ...phoneOwners].map((customer) => [customer._id, customer])).values()];
    if (owners.length === 0) return {owners, sharedContact: null};
    if (emailOwners.length > 0 && phoneOwners.length > 0) return {owners, sharedContact: SHARED_CONTACT.BOTH};
    return {owners, sharedContact: emailOwners.length > 0 ? SHARED_CONTACT.EMAIL : SHARED_CONTACT.PHONE};
  }, [isActive, hasMainMatches, byEmail.results, byPhone.results, draft]);

  const isFallbackLoading = !hasMainMatches && (byEmail.isLoading || byPhone.isLoading);

  return {
    matches: hasMainMatches ? main.results : fallback.owners,
    sharedContact: fallback.sharedContact,
    isLoading: isActive && (main.isLoading || isFallbackLoading),
    isActive,
  };
}
